import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, Search } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export function RadioDropdown({ value, onChange, options, placeholder, testid, disabled, searchable = true }) {
  const { t } = useContent();
  const ph = placeholder ?? t("dropdown.placeholder", "Sélectionner");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (open) { setQuery(""); setTimeout(() => searchRef.current?.focus(), 50); }
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const showSearch = searchable && options.length > 4;

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = norm(query);
    return options.filter((o) => norm(o.label).includes(q) || norm(o.sub).includes(q));
  }, [options, query]);

  return (
    <div className="relative" ref={ref}>
      <button type="button" disabled={disabled} data-testid={testid} onClick={() => setOpen((o) => !o)}
        className={`flex w-full items-center justify-between gap-2 rounded-xl border bg-card px-4 py-3 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${open ? "border-accent ring-2 ring-accent/30" : "border-border hover:border-accent/60"}`}>
        <span className={`truncate ${selected ? "text-foreground" : "text-muted-foreground"}`}>{selected ? selected.label : ph}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-border bg-popover shadow-xl" data-testid={`${testid}-list`}>
          {showSearch && (
            <div className="sticky top-0 flex items-center gap-2 border-b border-border bg-popover px-3 py-2">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} data-testid={`${testid}-search`}
                placeholder={t("dropdown.search", "Rechercher...")} className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground" />
            </div>
          )}
          <div className="max-h-64 overflow-auto p-1.5">
            {options.length === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground">{t("dropdown.no_option", "Aucune option disponible")}</div>}
            {options.length > 0 && filtered.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid={`${testid}-no-result`}>{t("dropdown.no_result", "Aucun résultat")}</div>
            )}
            {filtered.map((o) => {
              const active = o.value === value;
              return (
                <button key={o.value} type="button" data-testid={`${testid}-option-${o.value}`}
                  onClick={() => { onChange(o.value); setOpen(false); }}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${active ? "bg-accent/10" : "hover:bg-secondary"}`}>
                  <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${active ? "border-accent" : "border-muted-foreground/40"}`}>
                    {active && <span className="h-2 w-2 rounded-full bg-accent" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-foreground">{o.label}</span>
                    {o.sub && <span className="block truncate text-xs text-muted-foreground">{o.sub}</span>}
                  </span>
                  {o.right && <span className="shrink-0 font-display text-sm font-bold text-foreground">{o.right}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
